import React, { useEffect, useMemo } from "react";
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  Popconfirm,
  Select,
  Checkbox,
  Divider,
  Tooltip,
  Space,
  Popover,
  Tag,
  ConfigProvider,
} from "antd";
import esES from "antd/locale/es_ES";
import { UserCell } from "../../Components/UserAvatar";
import { fuzzyIncludes } from "../../utils/fuzzyMatch";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import AdminPasswordTools from "../../Components/AdminPasswordTools";
import TelefonoInput from "../../Components/TelefonoInput";
import AvisoTelefonosRepetidos from "../../Components/AvisoTelefonosRepetidos";
import { useAseosLogic } from "../aseos/useAseosLogic";
import { useAuth } from "../../context/AuthContext";
import { formatRut } from "../../utils/formatRut";
import { formatPhoneConCodigo } from "../../utils/formatPhone";
import { reglaTelefono } from "../../utils/telefonoE164";
import { encontrarTelefonosRepetidos } from "../../utils/telefonosRepetidos";
import { passwordMinRule } from "../../utils/passwordPolicy";
import { configOptionApi } from "../../services/configOption.api";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";

/**
 * Página /aseadores — CRUD de aseadores (round 2026-07-06).
 *
 * Antes esta gestión vivía como un sub-tab ("Gestión de aseadores") dentro de
 * /aseos, mezclada con la agenda de aseos. Se separó a su propia página bajo
 * "Gestión" (junto a Clientes y Trabajadores) para que /aseos quede solo con
 * los aseos (Operaciones). Reusa `useAseosLogic` (el hook maneja tanto aseos
 * como aseadores); acá solo renderizamos la UI de aseadores.
 *
 * El botón "Ver aseos (N)" navega a /aseos?aseador=<nombre> (deep-link que
 * /aseos interpreta pre-filtrando por ese aseador) — clave para reasignar sus
 * aseos antes de poder eliminarlo.
 */
const AseadoresPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { Option } = Select;

  const {
    aseadoras,
    filteredAseadoras,
    loading,
    modalVisible,
    savingAseadora,
    editingAseadora,
    sidebarOpen,
    form,
    misAseos,
    aseosTerminados,
    aseadoresConAseos,
    isAdmin,
    sectors,
    availableBanks,
    setAvailableBanks,
    accountTypes,
    setAccountTypes,
    toggleSidebar,
    showModal,
    handleCancel,
    handleSubmit,
    handleDelete,
    setAseadorasSearchText,
  } = useAseosLogic(user);

  // Aseadoras que comparten teléfono, sobre la lista COMPLETA (la búsqueda y los filtros no esconden un repetido). El asistente de
  // WhatsApp identifica por el número; el servidor ya impide crear un duplicado nuevo, pero los antiguos siguen ahí.
  const gruposTelefonoRepetido = useMemo(
    () =>
      encontrarTelefonosRepetidos(
        aseadoras.map((a) => ({ id: a.id, nombre: a.nombre, telefono: a.telefono, rol: "aseador" })),
      ),
    [aseadoras],
  );

  // Al EDITAR no se exige un dato que la aseadora no tenía ni se está cambiando (antes, para corregir solo un teléfono había
  // que completar RUT, banco, tipo y número de cuenta). Al crear se exige todo; y uno que ya tenía no se puede dejar vacío.
  const exigeAlGuardar = (valorOriginal: unknown): boolean =>
    !editingAseadora ||
    (Array.isArray(valorOriginal) ? valorOriginal.length > 0 : Boolean(valorOriginal));

  // Conteo de aseos asignados por aseador (id → cantidad). misAseos
  // (pendiente/en_proceso) y aseosTerminados (completado/pagado) son disjuntos
  // por estado → cubren todos los aseos asignados sin doble conteo.
  const aseosCountByAseador = useMemo(() => {
    const m = new Map<number, number>();
    [...misAseos, ...aseosTerminados].forEach((a: any) => {
      if (a?.idAseador != null) {
        const k = Number(a.idAseador);
        m.set(k, (m.get(k) ?? 0) + 1);
      }
    });
    return m;
  }, [misAseos, aseosTerminados]);

  // ── Filtros de la tabla (se combinan en AND con la búsqueda por nombre) ──
  // Los cuatro se recuerdan por usuario admin en este navegador. La búsqueda
  // vive acá y NO en `useAseosLogic`: ese hook también lo monta /aseos, donde
  // el mismo texto recortaría el desplegable de aseadoras del modal de aseo.
  const soloTextos = (v: unknown) =>
    Array.isArray(v) && v.every((x) => typeof x === "string");
  const [busqueda, setBusqueda] = useFiltroPersistido(
    "aseadores.busqueda",
    "",
    { validar: (v) => typeof v === "string" },
  );
  const [sectorFilter, setSectorFilter] = useFiltroPersistido<string[]>(
    "aseadores.sector",
    [],
    { validar: soloTextos },
  );
  const [bancoFilter, setBancoFilter] = useFiltroPersistido<string[]>(
    "aseadores.banco",
    [],
    { validar: soloTextos },
  );
  const [tipocuentaFilter, setTipocuentaFilter] = useFiltroPersistido<string[]>(
    "aseadores.tipoCuenta",
    [],
    { validar: soloTextos },
  );

  // El filtrado por nombre lo hace `useAseosLogic` (filteredAseadoras); acá
  // solo le pasamos el texto, incluido el que se restauró al montar.
  useEffect(() => {
    setAseadorasSearchText(busqueda);
  }, [busqueda, setAseadorasSearchText]);

  // Opciones derivadas de los datos reales → solo aparecen valores presentes
  // (nunca un filtro que deje la tabla vacía "por sorpresa").
  const sectorOptions = useMemo(
    () =>
      Array.from(new Set(aseadoras.flatMap((a) => a.sector ?? []))).sort(
        (x, y) => x.localeCompare(y),
      ),
    [aseadoras],
  );
  const bancoOptions = useMemo(
    () =>
      Array.from(
        new Set(aseadoras.map((a) => a.banco).filter(Boolean) as string[]),
      ).sort((x, y) => x.localeCompare(y)),
    [aseadoras],
  );
  const tipocuentaOptions = useMemo(
    () =>
      Array.from(
        new Set(aseadoras.map((a) => a.tipocuenta).filter(Boolean) as string[]),
      ).sort((x, y) => x.localeCompare(y)),
    [aseadoras],
  );

  // Aplica los filtros ENCIMA de la búsqueda por nombre (filteredAseadoras).
  // Sector = coincide si el aseador tiene ALGUNO de los sectores elegidos.
  const visibleAseadoras = useMemo(() => {
    return filteredAseadoras.filter((a: any) => {
      if (
        sectorFilter.length &&
        !(a.sector ?? []).some((s: string) => sectorFilter.includes(s))
      )
        return false;
      if (bancoFilter.length && !bancoFilter.includes(a.banco)) return false;
      if (tipocuentaFilter.length && !tipocuentaFilter.includes(a.tipocuenta))
        return false;
      return true;
    });
  }, [filteredAseadoras, sectorFilter, bancoFilter, tipocuentaFilter]);

  const filtrosActivos =
    sectorFilter.length + bancoFilter.length + tipocuentaFilter.length > 0;
  const limpiarFiltros = () => {
    setSectorFilter([]);
    setBancoFilter([]);
    setTipocuentaFilter([]);
  };

  // Deep-link a /aseos filtrado por este aseador (para reasignar/quitar sus
  // aseos y luego poder eliminarlo). /aseos lee `?aseador=` y `?tab=`.
  // Aterrizamos en la pestaña que REALMENTE tiene sus aseos: "Por hacer" (2) si
  // tiene pendientes/en proceso, si no "Terminados" (3). El conteo "Ver aseos"
  // incluye terminados, así que un aseador con solo aseos completados/pagados
  // debe aterrizar en la pestaña 3, no en una "Por hacer" vacía.
  const verAseosDeAseador = (record: any) => {
    const nombre = record?.nombre;
    if (!nombre) return;
    const enPorHacer = misAseos.some((a: any) => a.nombreAseador === nombre);
    const tab = enPorHacer ? "2" : "3";
    navigate(`/aseos?aseador=${encodeURIComponent(nombre)}&tab=${tab}`);
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      render: (nombre: string, record: any) => (
        <UserCell
          nombre={nombre}
          avatarSeed={record?.avatar}
          avatarUrl={record?.avatar_url}
          size={30}
        />
      ),
    },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "RUT", dataIndex: "rut", key: "rut" },
    {
      title: "Teléfono",
      dataIndex: "telefono",
      key: "telefono",
      render: (v: string) => formatPhoneConCodigo(v),
    },
    {
      title: "Sectores",
      dataIndex: "sector",
      key: "sector",
      // Ancho acotado: sin esto, un aseador con muchos sectores estiraba la
      // celda (join(", ") en una sola línea) y empujaba el resto de columnas
      // fuera de vista, obligando a scroll horizontal. Mostramos los primeros
      // como tags + "+N" con popover que lista todos al pasar el mouse.
      width: 300,
      render: (sectors: string[]) => {
        if (!sectors || sectors.length === 0)
          return <span style={{ color: "#999" }}>No asignado</span>;
        const MAX = 3;
        const shown = sectors.slice(0, MAX);
        const hidden = sectors.length - shown.length;
        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 4,
              alignItems: "center",
              maxWidth: 300,
            }}
          >
            {shown.map((s) => (
              <Tag key={s} color="purple" style={{ margin: 0 }}>
                {s}
              </Tag>
            ))}
            {hidden > 0 && (
              <Popover
                title="Todos los sectores"
                trigger="hover"
                content={
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 4,
                      maxWidth: 320,
                    }}
                  >
                    {sectors.map((s) => (
                      <Tag key={s} color="purple" style={{ margin: 0 }}>
                        {s}
                      </Tag>
                    ))}
                  </div>
                }
              >
                <Tag style={{ margin: 0, cursor: "pointer" }}>+{hidden}</Tag>
              </Popover>
            )}
          </div>
        );
      },
    },
    { title: "Banco", dataIndex: "banco", key: "banco" },
    { title: "Tipo de Cuenta", dataIndex: "tipocuenta", key: "tipocuenta" },
    { title: "Número de Cuenta", dataIndex: "numerocuenta", key: "numerocuenta" },
    {
      title: "Acciones",
      key: "acciones",
      render: (_: any, record: any) => {
        const numAseos = aseosCountByAseador.get(Number(record.id)) ?? 0;
        const bloqueado = aseadoresConAseos.has(Number(record.id));
        const plural = numAseos === 1 ? "" : "s";

        return (
          <>
            {isAdmin && (
              <Space size={0} wrap>
                <Tooltip title="Editar aseador">
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => showModal(record)}
                    type="link"
                  />
                </Tooltip>

                {numAseos > 0 && (
                  <Tooltip
                    title={`Ver y gestionar los ${numAseos} aseo${plural} de este aseador`}
                  >
                    <Button
                      type="link"
                      icon={<UnorderedListOutlined />}
                      onClick={() => verAseosDeAseador(record)}
                    >
                      Ver aseos ({numAseos})
                    </Button>
                  </Tooltip>
                )}

                {bloqueado ? (
                  <Popover
                    content={
                      <span>
                        No puedes eliminar: este aseador tiene {numAseos} aseo
                        {plural} asignado{plural}.
                        <br />
                        Usa <strong>"Ver aseos ({numAseos})"</strong> para
                        reasignarlos o quitarle el aseador; al quedar en 0
                        podrás eliminarlo.
                      </span>
                    }
                  >
                    <Button icon={<DeleteOutlined />} danger type="link" disabled />
                  </Popover>
                ) : (
                  <Popconfirm
                    title="¿Está seguro de eliminar esta aseadora?"
                    onConfirm={() => handleDelete(record.id)}
                    okText="Sí"
                    cancelText="No"
                  >
                    <Button icon={<DeleteOutlined />} danger type="link" />
                  </Popconfirm>
                )}
              </Space>
            )}
          </>
        );
      },
    },
  ];

  return (
    <ConfigProvider locale={esES}>
      <Layout
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        containerClass="home-container"
        contentClass="home-content"
      >
        <section className="content-section">
          <PageHeader
            title="Gestión de aseadores"
            helpText="Crea y administra aseadores, asigna sectores y datos de pago. La agenda de aseos está en Operaciones → Aseos."
          />
          <div className="header">
            <div className="header-buttons">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showModal()}
                style={{ marginRight: "10px" }}
              >
                Nuevo aseador
              </Button>
            </div>
          </div>

          <AvisoTelefonosRepetidos grupos={gruposTelefonoRepetido} />

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <Input.Search
              placeholder="Buscar por nombre"
              allowClear
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              style={{ flex: "1 1 240px", maxWidth: 340 }}
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Sectores"
              value={sectorFilter}
              onChange={setSectorFilter}
              maxTagCount="responsive"
              style={{ flex: "1 1 200px", minWidth: 170 }}
              filterOption={(input, option) =>
                fuzzyIncludes(String(option?.value ?? ""), input)
              }
              options={sectorOptions.map((s) => ({ label: s, value: s }))}
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Banco"
              value={bancoFilter}
              onChange={setBancoFilter}
              maxTagCount="responsive"
              style={{ flex: "1 1 180px", minWidth: 150 }}
              filterOption={(input, option) =>
                fuzzyIncludes(String(option?.value ?? ""), input)
              }
              options={bancoOptions.map((s) => ({ label: s, value: s }))}
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Tipo de cuenta"
              value={tipocuentaFilter}
              onChange={setTipocuentaFilter}
              maxTagCount="responsive"
              style={{ flex: "1 1 180px", minWidth: 150 }}
              filterOption={(input, option) =>
                fuzzyIncludes(String(option?.value ?? ""), input)
              }
              options={tipocuentaOptions.map((s) => ({ label: s, value: s }))}
            />
            {filtrosActivos && (
              <Button onClick={limpiarFiltros}>Limpiar filtros</Button>
            )}
          </div>

          <Table
            columns={columns}
            dataSource={visibleAseadoras}
            rowKey="id"
            loading={loading}
            scroll={{ x: "max-content" }}
            footer={() => (
              <span style={{ color: "var(--text-muted, #64748B)" }}>
                {visibleAseadoras.length}{" "}
                {visibleAseadoras.length === 1 ? "aseador" : "aseadores"}
                {visibleAseadoras.length !== aseadoras.length
                  ? ` (de ${aseadoras.length})`
                  : ""}
              </span>
            )}
          />

          {/* Modal crear/editar aseador (movido desde /aseos). */}
          {isAdmin && (
            <Modal
              title={editingAseadora ? "Editar aseador" : "Nuevo aseador"}
              open={modalVisible}
              onOk={handleSubmit}
              onCancel={handleCancel}
              okText={editingAseadora ? "Actualizar" : "Crear"}
              cancelText="Cancelar"
              confirmLoading={savingAseadora}
              width={700}
            >
              <Form form={form} layout="vertical">
                <div className="flex-container">
                  <Form.Item
                    name="nombre"
                    label="Nombre completo"
                    rules={[
                      {
                        required: true,
                        message: "Por favor ingrese el nombre completo",
                      },
                    ]}
                  >
                    <Input placeholder="Ej: María Rodríguez" />
                  </Form.Item>

                  <Form.Item
                    name="rut"
                    label="RUT"
                    rules={[
                      {
                        required: exigeAlGuardar(editingAseadora?.rut),
                        message: "Por favor ingrese el RUT",
                      },
                    ]}
                  >
                    <Input
                      placeholder="11.111.111-1"
                      maxLength={12}
                      onChange={(e) =>
                        form.setFieldsValue({ rut: formatRut(e.target.value) })
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: "Por favor ingrese el email" },
                      { type: "email", message: "Ingrese un email válido" },
                    ]}
                  >
                    <Input placeholder="correo@ejemplo.com" />
                  </Form.Item>

                  <Form.Item
                    name="telefono"
                    label="Teléfono"
                    rules={[
                      reglaTelefono({
                        obligatorio: true,
                        valorInicial: editingAseadora?.telefono,
                        mensajeObligatorio: "Por favor ingrese el teléfono, con su código de país",
                      }),
                    ]}
                  >
                    <TelefonoInput ariaLabel="Teléfono del aseador" />
                  </Form.Item>

                  <Form.Item
                    className="full-width"
                    name="password"
                    label={
                      editingAseadora
                        ? "Nueva Contraseña (dejar en blanco para mantener)"
                        : "Contraseña"
                    }
                    rules={[{ required: false }, passwordMinRule]}
                  >
                    <Input.Password placeholder="Contraseña" />
                  </Form.Item>

                  <div className="full-width">
                    <AdminPasswordTools
                      form={form}
                      userId={editingAseadora?.id}
                      currentEmail={editingAseadora?.email}
                    />
                  </div>

                  <Form.Item
                    name="rol"
                    label="Roles"
                    initialValue={["aseador"]}
                    hidden={true}
                  >
                    <Checkbox.Group>
                      <Checkbox value="aseador" disabled={true}>
                        Aseador
                      </Checkbox>
                    </Checkbox.Group>
                  </Form.Item>

                  <Form.Item
                    className="full-width"
                    name="sector"
                    label="Sectores"
                    rules={[
                      {
                        required: exigeAlGuardar(editingAseadora?.sector),
                        message: "Por favor seleccione al menos un sector",
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Seleccione los sectores"
                      style={{ width: "100%" }}
                    >
                      {sectors.map((sector) => (
                        <Option key={sector.sector} value={sector.sector}>
                          {sector.sector}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="banco"
                    label="Banco"
                    rules={[
                      {
                        required: exigeAlGuardar(editingAseadora?.banco),
                        message: "Por favor seleccione un banco",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Seleccione un banco"
                      showSearch
                      optionFilterProp="children"
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <Divider style={{ margin: "8px 0" }} />
                          <div
                            style={{ display: "flex", gap: 8, padding: "0 8px 8px" }}
                          >
                            <Input
                              placeholder="Nuevo banco..."
                              id="new-banco-aseadores"
                              onPressEnter={async (e) => {
                                const input = e.currentTarget;
                                const val = input.value.trim();
                                if (!val) return;
                                await configOptionApi.create({
                                  categoria: "banco",
                                  valor: val,
                                });
                                setAvailableBanks((prev) => [...prev, val]);
                                form.setFieldValue("banco", val);
                                input.value = "";
                              }}
                            />
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={async () => {
                                const input = document.getElementById(
                                  "new-banco-aseadores",
                                ) as HTMLInputElement;
                                const val = input?.value.trim();
                                if (!val) return;
                                await configOptionApi.create({
                                  categoria: "banco",
                                  valor: val,
                                });
                                setAvailableBanks((prev) => [...prev, val]);
                                form.setFieldValue("banco", val);
                                if (input) input.value = "";
                              }}
                            >
                              Crear
                            </Button>
                          </div>
                        </>
                      )}
                    >
                      {availableBanks.map((bank) => (
                        <Option key={bank} value={bank}>
                          {bank}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="tipocuenta"
                    label="Tipo de Cuenta"
                    rules={[
                      {
                        required: exigeAlGuardar(editingAseadora?.tipocuenta),
                        message: "Por favor seleccione un tipo de cuenta",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Seleccione un tipo de cuenta"
                      showSearch
                      optionFilterProp="children"
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <Divider style={{ margin: "8px 0" }} />
                          <div
                            style={{ display: "flex", gap: 8, padding: "0 8px 8px" }}
                          >
                            <Input
                              placeholder="Nuevo tipo..."
                              id="new-tipocuenta-aseadores"
                              onPressEnter={async (e) => {
                                const input = e.currentTarget;
                                const val = input.value.trim();
                                if (!val) return;
                                await configOptionApi.create({
                                  categoria: "tipocuenta",
                                  valor: val,
                                });
                                setAccountTypes((prev) => [...prev, val]);
                                form.setFieldValue("tipocuenta", val);
                                input.value = "";
                              }}
                            />
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={async () => {
                                const input = document.getElementById(
                                  "new-tipocuenta-aseadores",
                                ) as HTMLInputElement;
                                const val = input?.value.trim();
                                if (!val) return;
                                await configOptionApi.create({
                                  categoria: "tipocuenta",
                                  valor: val,
                                });
                                setAccountTypes((prev) => [...prev, val]);
                                form.setFieldValue("tipocuenta", val);
                                if (input) input.value = "";
                              }}
                            >
                              Crear
                            </Button>
                          </div>
                        </>
                      )}
                    >
                      {accountTypes.map((type) => (
                        <Option key={type} value={type}>
                          {type}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="numerocuenta"
                    label="Número de Cuenta"
                    rules={[
                      {
                        required: exigeAlGuardar(editingAseadora?.numerocuenta),
                        message: "Por favor ingrese el número de cuenta",
                      },
                    ]}
                  >
                    <Input placeholder="Ej: 987654321" />
                  </Form.Item>
                </div>
              </Form>
            </Modal>
          )}
        </section>
      </Layout>
    </ConfigProvider>
  );
};

export default AseadoresPage;
